import type { ParseResult, ParsedMoment } from './parsers';
import { clockToVideo, type SyncConfig } from './timeline';

export type Origin = 'list' | 'mark' | 'manual';

/** One moment in video time. List moments get padding; marks and hand-made clips are used exactly. */
export interface Moment {
  id: string;
  origin: Origin;
  start: number;
  end: number;
  period?: number;
  clockSec?: number;
  label?: string;
}

export interface ClipSettings {
  padBefore: number;
  padAfter: number;
  mergeGap: number;
}

export interface Clip {
  id: string;
  origin: Origin;
  start: number;
  end: number;
  sourceIds: string[];
  period?: number;
  clockSec?: number;
  label?: string;
}

export interface ClipEdits {
  bounds: Record<string, { start: number; end: number }>;
  excluded: Record<string, boolean>;
  labels: Record<string, string>;
}

export interface ReelClip extends Clip {
  index: number;
  title: string;
  included: boolean;
  edited: boolean;
  /** Starts after the end of the video: can't be exported. */
  outside: boolean;
}

export const MIN_CLIP_LENGTH = 1;

export function listMoments(
  parsed: ParseResult,
  opts: { timeBase: 'match' | 'video'; sync: SyncConfig; defaultLength: number },
): { moments: Moment[]; unmapped: ParsedMoment[] } {
  const moments: Moment[] = [];
  const unmapped: ParsedMoment[] = [];
  for (const p of parsed.moments) {
    const video = opts.timeBase === 'video' ? p.timeSec : clockToVideo(p.period, p.timeSec, opts.sync);
    if (video === null) {
      unmapped.push(p);
      continue;
    }
    const length = p.durationSec ?? opts.defaultLength;
    moments.push({
      id: p.id,
      origin: 'list',
      start: video,
      end: video + length,
      period: opts.timeBase === 'video' ? undefined : p.period,
      clockSec: opts.timeBase === 'video' ? undefined : p.timeSec,
      label: p.label || undefined,
    });
  }
  return { moments, unmapped };
}

/**
 * Pads list moments, sorts them and merges overlapping or touching ones (clips closer than mergeGap),
 * exactly like veo-highlights.mjs. Marks and hand-made clips are kept as they are.
 */
export function buildClips(moments: Moment[], settings: ClipSettings): Clip[] {
  const list = moments
    .filter((m) => m.origin === 'list')
    .map((m) => ({ ...m, start: Math.max(0, m.start - settings.padBefore), end: m.end + settings.padAfter }))
    .sort((a, b) => a.start - b.start);
  const merged: Clip[] = [];
  for (const m of list) {
    const prev = merged[merged.length - 1];
    if (prev && m.start <= prev.end + settings.mergeGap) {
      prev.end = Math.max(prev.end, m.end);
      prev.sourceIds.push(m.id);
      if (!prev.label && m.label) prev.label = m.label;
    } else {
      merged.push({
        id: m.id,
        origin: 'list',
        start: m.start,
        end: m.end,
        sourceIds: [m.id],
        period: m.period,
        clockSec: m.clockSec,
        label: m.label,
      });
    }
  }
  const exact: Clip[] = moments
    .filter((m) => m.origin !== 'list')
    .map((m) => ({ id: m.id, origin: m.origin, start: m.start, end: m.end, sourceIds: [m.id], label: m.label }));
  return [...merged, ...exact].sort((a, b) => a.start - b.start);
}

export function applyEdits(clips: Clip[], edits: ClipEdits, videoDuration: number | null): ReelClip[] {
  const out = clips.map((c) => {
    const b = edits.bounds[c.id];
    let start = b ? b.start : c.start;
    let end = b ? b.end : c.end;
    const outside = videoDuration !== null && start >= videoDuration;
    if (videoDuration !== null && !outside) end = Math.min(end, videoDuration);
    start = Math.max(0, start);
    const label = edits.labels[c.id] ?? c.label ?? '';
    return {
      ...c,
      start,
      end,
      label,
      edited: !!b,
      outside,
      included: !outside && !edits.excluded[c.id],
      index: 0,
      title: '',
    };
  });
  out.sort((a, b) => a.start - b.start);
  out.forEach((c, i) => {
    c.index = i + 1;
    c.title = c.label || `Clip ${i + 1}`;
  });
  return out;
}

export function reelStats(reel: ReelClip[]): { count: number; total: number } {
  let total = 0;
  let count = 0;
  for (const c of reel) {
    if (!c.included) continue;
    count++;
    total += c.end - c.start;
  }
  return { count, total };
}

/** The ranges to export, in order. */
export function exportRanges(reel: ReelClip[]): { start: number; end: number }[] {
  return reel.filter((c) => c.included && c.end - c.start > 0.05).map((c) => ({ start: c.start, end: c.end }));
}
